<template>
    <Transition name="wheel-fade">
        <div v-if="visible" role="status" aria-live="polite" class="absolute inset-0 z-50 flex flex-col items-center justify-center bg-gray-50">
            <div class="flex flex-col items-center">
                <svg :width="SIZE" :height="SIZE" :viewBox="`0 0 ${SIZE} ${SIZE}`" aria-hidden="true">
                    <defs>
                        <clipPath id="petal-clip">
                            <path :d="petalClipPath" clip-rule="evenodd" />
                        </clipPath>
                        <!-- Mask hides the last circle where it overlaps the first to complete the cyclic fold-under -->
                        <mask id="fold-under" maskUnits="userSpaceOnUse">
                            <rect :width="SIZE" :height="SIZE" fill="white" />
                            <circle :cx="circlePositions[SEGMENT_COUNT - 1].cx" :cy="circlePositions[SEGMENT_COUNT - 1].cy" :r="effectiveCircleR" fill="black" />
                        </mask>
                    </defs>

                    <g clip-path="url(#petal-clip)">
                        <!--
                            Render order [last, 0, 1, ..., second-to-last] gives correct clockwise
                            fold-under stacking. Only the last junction needs a mask.
                        -->
                        <circle
                            v-for="idx in RENDER_ORDER"
                            :key="idx"
                            :cx="circlePositions[idx].cx"
                            :cy="circlePositions[idx].cy"
                            :r="effectiveCircleR"
                            :fill="circlePositions[idx].color"
                            :mask="idx === SEGMENT_COUNT - 2 && phase !== 'done' ? 'url(#fold-under)' : undefined" />
                    </g>

                    <!-- Center hub -->
                    <circle :cx="CENTER" :cy="CENTER" :r="HUB_R" :fill="hubFill" class="hub" />
                </svg>

                <!-- Type label -->
                <div class="mt-2 flex items-center gap-3 h-10" aria-hidden="true">
                    <template v-if="displayedIndex !== null">
                        <ArchetypeDot :letters="WHEEL_ORDER[displayedIndex]" class="size-5" />
                        <span class="text-3xl font-medium text-gray-900">{{ segments[displayedIndex].title }}</span>
                    </template>
                </div>

                <!-- Screen reader announcement (only when result is final) -->
                <span v-if="phase === 'done'" class="sr-only"> Your perspective type is {{ segments[targetIndex].title }} </span>
            </div>
        </div>
    </Transition>
</template>

<script setup lang="ts">
import { PERSPECTIVE_TYPES, TYPE_COLORS, WHEEL_ORDER } from "~vue/pages/perspectiveTypes";
import ArchetypeDot from "~vue/Perspective/ArchetypeDot.vue";
import { computed, onUnmounted, ref, watch } from "vue";

export interface Props {
    /** 4-letter type code the wheel should land on, e.g. "INTJ". */
    letters: string | null;
    visible: boolean;
}

const props = defineProps<Props>();
const emit = defineEmits<{ done: [] }>();

// ── Layout ──
// RING_R / CIRCLE_R > 2.613 guarantees only adjacent circles overlap.
const SIZE = 400;
const CENTER = SIZE / 2;
const RING_R = 135;
const CIRCLE_R = 50;
const HUB_R = 90;

// ── Timing (ms) ──
const BLOOM_MS = 500;
const SPIN_MS = 6500;
const REVEAL_HOLD_MS = 500;
const COLLAPSE_MS = 700;
const DONE_HOLD_MS = 1500;
const FULL_SPINS = 1;

// ── Segments ──
const SEGMENT_COUNT = WHEEL_ORDER.length;
const DEGREES_PER_SEGMENT = 360 / SEGMENT_COUNT;

/** Render [last, 0, 1, ..., second-to-last] so each circle folds under the next clockwise. */
const RENDER_ORDER = [SEGMENT_COUNT - 1, ...Array.from({ length: SEGMENT_COUNT - 1 }, (_, i) => i)];

const REVEALED_PHASES = new Set<string>(["reveal", "collapsing", "done"]);

const segments = WHEEL_ORDER.map((letters, i) => ({
    title: PERSPECTIVE_TYPES[letters]?.title ?? letters,
    color: TYPE_COLORS[letters] ?? "#ccc",
    baseAngle: (i / SEGMENT_COUNT) * Math.PI * 2 - Math.PI / 2,
}));

const targetIndex = computed(() => {
    if (!props.letters) {
        return 0;
    }
    const idx = WHEEL_ORDER.indexOf(props.letters as (typeof WHEEL_ORDER)[number]);
    return idx >= 0 ? idx : 0;
});

// ── Animation state ──
const phase = ref<"bloom" | "spinning" | "reveal" | "collapsing" | "done">("bloom");
const rotation = ref(0);
const ringScale = ref(0);
const collapseProgress = ref(0);
const displayedIndex = ref<number | null>(null);

let animFrame = 0;
let phaseTimer = 0;

/** Shared scale factor driving circle positions, radii, and clip path during bloom/collapse. */
const animScale = computed(() => ringScale.value * (1 - collapseProgress.value));

const circlePositions = computed(() => {
    const rotRad = (rotation.value * Math.PI) / 180;
    const scale = animScale.value;

    return segments.map((seg) => {
        const angle = seg.baseAngle + rotRad;
        return {
            cx: CENTER + RING_R * Math.cos(angle) * scale,
            cy: CENTER + RING_R * Math.sin(angle) * scale,
            color: seg.color,
        };
    });
});

const effectiveCircleR = computed(() => Math.max(CIRCLE_R * animScale.value, 1));

const petalClipPath = computed(() => {
    const r = Math.max(HUB_R * animScale.value, 1);
    return `M0,0 H${SIZE} V${SIZE} H0 Z M${CENTER + r},${CENTER} A${r},${r} 0 1,0 ${CENTER - r},${CENTER} A${r},${r} 0 1,0 ${CENTER + r},${CENTER}`;
});

const hubFill = computed(() => (REVEALED_PHASES.has(phase.value) ? segments[targetIndex.value]!.color : "white"));

// ── Animation helpers ──

/** Run a rAF loop with normalized progress [0, 1] and an easing function. */
function animate(duration: number, ease: (t: number) => number, onTick: (eased: number) => void, onDone: () => void) {
    const startTime = performance.now();
    function tick(now: number) {
        const t = Math.min((now - startTime) / duration, 1);
        onTick(ease(t));
        if (t < 1) {
            animFrame = requestAnimationFrame(tick);
        } else {
            onDone();
        }
    }
    animFrame = requestAnimationFrame(tick);
}

const easeOutCubic = (t: number) => 1 - (1 - t) ** 3;
const easeOutQuad = (t: number) => 1 - (1 - t) ** 2;
const easeInOutQuad = (t: number) => (t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2);

// ── Animation phases ──

function startAnimation() {
    phase.value = "bloom";
    rotation.value = 0;
    ringScale.value = 0;
    collapseProgress.value = 0;
    displayedIndex.value = null;

    animate(
        BLOOM_MS,
        easeOutCubic,
        (v) => (ringScale.value = v),
        () => {
            ringScale.value = 1;
            phase.value = "spinning";
            startSpin();
        },
    );
}

function startSpin() {
    const targetAngle = FULL_SPINS * 360 + targetIndex.value * DEGREES_PER_SEGMENT;

    animate(
        SPIN_MS,
        easeOutQuad,
        (eased) => {
            rotation.value = eased * targetAngle;
            displayedIndex.value = Math.floor(rotation.value / DEGREES_PER_SEGMENT) % SEGMENT_COUNT;
        },
        () => {
            displayedIndex.value = targetIndex.value;
            phase.value = "reveal";
            phaseTimer = globalThis.setTimeout(startCollapse, REVEAL_HOLD_MS);
        },
    );
}

function startCollapse() {
    phase.value = "collapsing";

    animate(
        COLLAPSE_MS,
        easeInOutQuad,
        (v) => (collapseProgress.value = v),
        () => {
            collapseProgress.value = 1;
            phase.value = "done";
            phaseTimer = globalThis.setTimeout(() => emit("done"), DONE_HOLD_MS);
        },
    );
}

function cleanup() {
    cancelAnimationFrame(animFrame);
    clearTimeout(phaseTimer);
}

watch(
    () => props.visible,
    (v) => (v ? startAnimation() : cleanup()),
    { immediate: true },
);

onUnmounted(cleanup);
</script>

<style scoped>
.hub {
    transition: fill 0.5s ease;
}

.wheel-fade-enter-active {
    transition: opacity 0.3s ease;
}
.wheel-fade-enter-from {
    opacity: 0;
}
</style>
